/**
 * onePagerFormatters.ts
 * Pure formatting and mapping utilities for the KPI One Pager V2.
 * Extracted for reuse across table, tooltips, and export features.
 */

import { GrainKey, PagerColumn, SparcCategory } from '../constants/onePagerConstants';
import type { NavigableFilterType } from '../types/onePagerTypes';
import { formatKpiValueByName, formatKpiTargetByName, type KpiFormat } from './formatters';

// ─── KPI Value Formatting ─────────────────────────────────────────────────────

/**
 * Format a numeric KPI value for display based on column format type.
 * Returns '—' for undefined/null/NaN values.
 */
const normalizeMetricToken = (value: string): string =>
	String(value).trim().toLowerCase().replace(/[%$\s_\-]/g, '');

const PERCENT_METRICS = new Set<string>([
	'ot',
	'turnover',
	'turn',
	'sia',
	'bpa',
	'ihc',
	'inhousecut',
	'ontime',
	'fillrate',
	'haulback',
	'layer',
	'pte',
	'pte_execution',
	'perfecttruckexecution',
	'dps',
	'pku',
	'repack',
	'whbreak',
	'mlp',
	'undirectedhours',
]);

const NUMBER_METRICS = new Set<string>([
	'kcph',
	'lcph',
	'layercph',
]);

const CURRENCY_METRICS = new Set<string>([
	'allinwaste',
	'waste',
	'rapidbreak',
	'directlaborcost',
]);

const getKpiFormatForColumn = (col: PagerColumn): KpiFormat => {
	const idToken = normalizeMetricToken(col.id);
	const labelToken = normalizeMetricToken(col.label);

	if (CURRENCY_METRICS.has(idToken) || CURRENCY_METRICS.has(labelToken)) {
		return 'currency';
	}

	if (PERCENT_METRICS.has(idToken) || PERCENT_METRICS.has(labelToken)) {
		return 'percent';
	}

	if (NUMBER_METRICS.has(idToken) || NUMBER_METRICS.has(labelToken)) {
		return 'number';
	}

	if (col.fmt.startsWith('usd')) return 'currency';
	if (col.fmt.startsWith('pct')) return 'percent';
	return 'number';
};

const getMetricDisplayName = (col: PagerColumn): string => col.label || col.id;

export const formatKpiValue = (value: number | null | undefined, col: PagerColumn): string => {
	if (value === undefined || value === null || isNaN(value)) return '—';

	const format = getKpiFormatForColumn(col);
	return formatKpiValueByName(value, format, getMetricDisplayName(col));
};

/**
 * Format target values using the same name-aware rules used by KPI Overview.
 * This keeps One Pager rows, totals, and tooltips consistent with shared KPI formatting.
 */
export const formatTargetValue = (value: number | null | undefined, col: PagerColumn): string => {
	if (value === undefined || value === null || isNaN(value)) return '—';

	const format = getKpiFormatForColumn(col);
	return formatKpiTargetByName(value, format, getMetricDisplayName(col));
};

// ─── Grain → Navigation Mapping ──────────────────────────────────────────────

/**
 * Map a grain key to its corresponding navigation filter type.
 * Returns null for non-navigable dimensions (year, period, week, date, location type).
 */
export const grainKeyToFilterType = (key: GrainKey): NavigableFilterType | null => {
	switch (key) {
		case 'rg':
			return 'region';
		case 'mk':
			return 'market';
		case 'lc':
			return 'location';
		default:
			return null;
	}
};

// ─── SPARC Category Style Key ────────────────────────────────────────────────

/** Style key suffix for SPARC category (used to look up style objects) */
export type SparcStyleKey = 'S' | 'P' | 'A' | 'R' | 'C' | 'N';

/**
 * Get the style key for a SPARC category.
 * Used by components to select the correct category background style.
 */
export const getSparcStyleKey = (sparc: SparcCategory): SparcStyleKey => sparc;
